import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { byPrefixAndName } from "@awesome.me/kit-8789beac8f/icons";
import { useTheme } from "@/providers/ThemeProvider";

interface ChatIconProps {
  className?: string;
  iconstring?: string;
  size?: number;
  color?: string;
  bg?: string; // Tailwind color class or CSS color
  hoverColor?: string;
  style?: "solid" | "regular" | "light" | "thin" | "brands" | "kit" | "duotone" | "auto"; // Font Awesome style
}

const IconProvider: React.FC<ChatIconProps> = ({
  className = "",
  size = 24,
  color,
  bg,
  iconstring,
  hoverColor,
  style = "auto",
}) => {
  const { effectiveTheme } = useTheme();
  
  // Get theme-aware default color from CSS variables
  // If no color prop is provided, use the foreground color from the theme
  const getDefaultColor = () => {
    if (color) return color;
    
    // Get the computed foreground color from CSS variables
    const root = document.documentElement;
    const computedStyle = getComputedStyle(root);
    const foregroundHsl = computedStyle.getPropertyValue('--foreground').trim();
    
    // Return HSL color, or fallback based on theme
    if (foregroundHsl) {
      return `hsl(${foregroundHsl})`;
    }
    
    // Fallback colors if CSS variables aren't available
    return effectiveTheme === 'dark' ? '#e5e5e5' : '#111';
  };

  const iconColor = getDefaultColor();

  // Convert icon string to FontAwesome object format using custom kit
  const getIconProp = (icon: string | undefined): IconProp | undefined => {
    if (!icon) return undefined;

    // Remove any existing prefixes and get just the icon name
    const iconName = icon
      .replace(/^fa-sharp fa-light fa-/, "")
      .replace(/^fa-/, "");

    // Map style names to FontAwesome prefixes
    const styleToPrefix: Record<string, string> = {
      solid: "fas",
      regular: "far", 
      light: "fal",
      thin: "fat",
      brands: "fab",
      kit: "fak",
      duotone: "fakd"
    };

    // If a specific style is requested, try that first
    if (style !== "auto" && styleToPrefix[style]) {
      const prefix = styleToPrefix[style];
      const iconDef = byPrefixAndName[prefix]?.[iconName];
      if (iconDef) {
        return iconDef;
      }
    }

    // Auto mode: try different prefixes available in the custom kit
    const prefixes = ["fas", "far", "fal", "fat", "fab", "fak", "fakd"];

    for (const prefix of prefixes) {
      const iconDef = byPrefixAndName[prefix]?.[iconName];
      if (iconDef) {
        return iconDef;
      }
    }

    // Fallback to regular style if no icon found
    console.warn(
      `Icon "${iconName}" not found in custom kit, falling back to far`
    );
    return ["far", iconName] as IconProp;
  };

  const iconProp = getIconProp(iconstring);

  const iconComponent = (
    <FontAwesomeIcon
      icon={iconProp}
      style={{
        fontSize: `${size}px !important`,
        color: iconColor,
        width: `${size}px`,
        height: `${size}px`,
      }}
      className={className}
    />
  );

  if (bg !== undefined) {
    const bgClass = bg === "" ? "bg-transparent" : bg;

    // Use CSS custom property for hover color
    const hoverStyle = hoverColor
      ? ({
          "--hover-bg-color": hoverColor,
        } as React.CSSProperties)
      : {};

    return (
      <span
        className={`inline-flex items-center justify-center rounded ${bgClass} transition-colors duration-200 relative z-10 ${
          hoverColor ? "hover:bg-[var(--hover-bg-color)]" : ""
        }`}
        style={{
          width: size + 12,
          height: size + 12,
          cursor: hoverColor ? "pointer" : "default",
          ...hoverStyle,
        }}
      >
        {iconComponent}
      </span>
    );
  }

  // Handle hover for icons without background
  if (hoverColor) {
    const hoverStyle = {
      "--hover-bg-color": hoverColor,
    } as React.CSSProperties;

    return (
      <span
        className="inline-flex items-center justify-center rounded hover:bg-[var(--hover-bg-color)] transition-colors duration-200 relative z-10"
        style={{
          width: size + 12,
          height: size + 12,
          cursor: "pointer",
          ...hoverStyle,
        }}
      >
        {iconComponent}
      </span>
    );
  }

  return iconComponent;
};

export default IconProvider;
